/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { businessPolicyService } from "@src/@api-services/businessPolicy";
import { MANUAL_REFUND_ALLOWED } from "../constants";
import { TableSort, type TableSortValue } from "@src/shared/constants/sort-order.constants";

// ─── Types ────────────────────────────────────
export interface BusinessPolicy {
  id: string | number;
  businessId: string | number;
  businessName: string;
  platformFee: number;
  isManualRefundAllowed: (typeof MANUAL_REFUND_ALLOWED)[keyof typeof MANUAL_REFUND_ALLOWED];
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

interface GetBusinessPoliciesParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  orderBy?: string;
  orderDirection?: TableSortValue;
  [key: string]: any;
}

interface BusinessPoliciesState {
  data: BusinessPolicy[];
  total: number;
  params: GetBusinessPoliciesParams;
  selectedBusinessPolicy: BusinessPolicy | null;
  businessesDropdown: { id: number; businessName: string }[];
  loading: boolean;
  error: string | null;
}

// ─── Async Thunks ─────────────────────────────
export const getBusinessPolicies = createAsyncThunk(
  "businessPolicies/getBusinessPolicies",
  async (params: GetBusinessPoliciesParams, { rejectWithValue }) => {
    try {
      const response = await businessPolicyService.getBusinessPolicies(params);
      return {
        params,
        data: response.data.data.data,
        total: response.data.data.totalRecord,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch business policies",
      );
    }
  },
);

export const getBusinessPolicy = createAsyncThunk(
  "businessPolicies/getBusinessPolicy",
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await businessPolicyService.getBusinessPolicy(id);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch business policy",
      );
    }
  },
);

export const createBusinessPolicy = createAsyncThunk(
  "businessPolicies/createBusinessPolicy",
  async (data: any, { dispatch, getState, rejectWithValue }: any) => {
    try {
      const response = await businessPolicyService.createBusinessPolicy(data);
      const state = getState();
      await dispatch(getBusinessPolicies(state.businessPolicies.params));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create business policy",
      );
    }
  },
);

export const updateBusinessPolicy = createAsyncThunk(
  "businessPolicies/updateBusinessPolicy",
  async (
    { id, data }: { id: string | number; data: any },
    { dispatch, getState, rejectWithValue }: any,
  ) => {
    try {
      const response = await businessPolicyService.updateBusinessPolicy(
        id,
        data,
      );
      const state = getState();
      await dispatch(getBusinessPolicies(state.businessPolicies.params));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update business policy",
      );
    }
  },
);

export const deleteBusinessPolicy = createAsyncThunk(
  "businessPolicies/deleteBusinessPolicy",
  async (id: string | number, { dispatch, getState, rejectWithValue }: any) => {
    try {
      await businessPolicyService.deleteBusinessPolicy(id);
      const state = getState();
      await dispatch(getBusinessPolicies(state.businessPolicies.params));
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete business policy",
      );
    }
  },
);

export const getBusinessesDropdown = createAsyncThunk(
  "businessPolicies/getBusinessesDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await businessPolicyService.getBusinessesDropdown();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch businesses",
      );
    }
  },
);

// ─── Initial State ─────────────────────────────
const initialState: BusinessPoliciesState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "createdAt",
    orderDirection: TableSort.DESC,
  },
  selectedBusinessPolicy: null,
  businessesDropdown: [],
  loading: false,
  error: null,
};

// ─── Slice ─────────────────────────────────────
export const businessPoliciesSlice = createSlice({
  name: "businessPolicies",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedBusinessPolicy: (state) => {
      state.selectedBusinessPolicy = null;
    },
    setParams: (state, action) => {
      state.params = { ...state.params, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBusinessPolicies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBusinessPolicies.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.params = action.payload.params;
        state.total = action.payload.total;
      })
      .addCase(getBusinessPolicies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getBusinessPolicy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBusinessPolicy.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedBusinessPolicy = action.payload;
      })
      .addCase(getBusinessPolicy.rejected, (state) => {
        state.loading = false;
        state.selectedBusinessPolicy = null;
      })
      .addCase(getBusinessesDropdown.fulfilled, (state, action) => {
        state.businessesDropdown = action.payload;
      });
  },
});

export const { clearError, clearSelectedBusinessPolicy, setParams } =
  businessPoliciesSlice.actions;
export default businessPoliciesSlice.reducer;
